'use client';

import React, { useEffect } from 'react';
import Script from 'next/script';
import { clearCookies, clearHotjarStorage, HOTJAR_COOKIES } from './trackerCookies';
import { useConsent } from './useConsent';

/**
 * Loads Hotjar, and only once someone has agreed to being recorded.
 *
 * This is the most intrusive thing the site does, and it is worth being plain
 * about why it has its own question rather than sitting under analytics.
 * Counting page views is aggregate; Hotjar records an individual visit —
 * movement, scrolling, clicks, hesitation — and plays it back. Somebody can
 * reasonably be happy to be counted and not happy to be watched, and a single
 * switch covering both would take the first answer as permission for the
 * second.
 *
 * Nothing is requested from Hotjar until that answer is yes. Withdrawal stops
 * the recording and removes what it stored, in cookies and in sessionStorage
 * both — Hotjar keeps state in both, so clearing only cookies would leave a
 * recording session alive.
 */
export default function Hotjar({ siteId }: { siteId: string }) {
  const { choice } = useConsent();
  const granted = choice?.behaviour === true;

  useEffect(() => {
    if (!choice || choice.behaviour) return;
    clearCookies(HOTJAR_COOKIES);
    clearHotjarStorage();
  }, [choice]);

  if (!granted) return null;

  return (
    <Script id="hotjar" strategy="afterInteractive">
      {`(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};` +
        `h._hjSettings={hjid:${siteId},hjsv:6};a=o.getElementsByTagName('head')[0];` +
        `r=o.createElement('script');r.async=1;` +
        `r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);` +
        `})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}
    </Script>
  );
}
